import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import StateLibrary from './StateLibrary';
import ArchetypeSelection from './ArchetypeSelection';
import MusicSelection from './MusicSelection';
import ImmersionExperience from './ImmersionExperience';
import AffirmationScreen from './AffirmationScreen';
import RecordState from './RecordState';
import AudioSettings from './AudioSettings';
import SaveState from './SaveState';
import RealityPlayer from './RealityPlayer';

// Flow steps
const STEPS = {
  LIBRARY: 'library',
  ARCHETYPE: 'archetype',
  MUSIC: 'music',
  IMMERSION: 'immersion',
  AFFIRMATION: 'affirmation',
  RECORD: 'record',
  SETTINGS: 'settings',
  SAVE: 'save',
  PLAYER: 'player'
};

const TuneInToReality = ({ onClose, savedStates = [], onSaveState }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.LIBRARY);
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [voiceRecording, setVoiceRecording] = useState(null);
  const [voiceRecordingUrl, setVoiceRecordingUrl] = useState(null);
  const [audioSettings, setAudioSettings] = useState(null);
  const [activePlayState, setActivePlayState] = useState(null);

  // Handle archetype selection
  const handleArchetypeSelect = (archetypeId) => {
    setSelectedArchetype(archetypeId);
    setCurrentStep(STEPS.MUSIC);
  };

  // Handle music selection
  const handleMusicSelect = (track) => {
    setSelectedMusic(track);
    setCurrentStep(STEPS.IMMERSION);
  };

  // Handle immersion complete
  const handleImmersionComplete = () => {
    setCurrentStep(STEPS.AFFIRMATION);
  };

  // Handle affirmation continue
  const handleAffirmationContinue = () => {
    setCurrentStep(STEPS.RECORD);
  };

  // Handle recording complete
  const handleRecordComplete = (recording) => {
    setVoiceRecording(recording);
    // Create and store the blob URL immediately
    if (recording) {
      const url = URL.createObjectURL(recording);
      setVoiceRecordingUrl(url);
    }
    setCurrentStep(STEPS.SETTINGS);
  };

  // Handle settings complete
  const handleSettingsComplete = (settings) => {
    setAudioSettings(settings);
    setCurrentStep(STEPS.SAVE);
  };

  // Handle save
  const handleSave = async (stateName) => {
    // Create the state object - use the stored blob URL
    const newState = {
      id: `state_${Date.now()}`,
      name: stateName,
      archetypeId: selectedArchetype,
      musicTrack: selectedMusic,
      voiceRecordingUrl: voiceRecordingUrl, // Use the stored URL
      settings: audioSettings,
      createdAt: new Date().toISOString()
    };

    // Call parent save handler
    if (onSaveState) {
      await onSaveState(newState);
    }

    // Show player with new state
    setActivePlayState(newState);
    setCurrentStep(STEPS.PLAYER);
  };

  // Handle play saved state
  const handlePlayState = (state) => {
    setActivePlayState(state);
    setCurrentStep(STEPS.PLAYER);
  };

  // Handle close player
  const handleClosePlayer = () => {
    setActivePlayState(null);
    setCurrentStep(STEPS.LIBRARY);
    // Reset creation flow (but don't revoke the URL if it's still needed)
    setSelectedArchetype(null);
    setSelectedMusic(null);
    setVoiceRecording(null);
    setVoiceRecordingUrl(null);
    setAudioSettings(null);
  };

  // Back handlers
  const handleBackToLibrary = () => setCurrentStep(STEPS.LIBRARY);
  const handleBackToArchetype = () => setCurrentStep(STEPS.ARCHETYPE);
  const handleBackToMusic = () => setCurrentStep(STEPS.MUSIC);
  const handleBackToRecord = () => setCurrentStep(STEPS.RECORD);
  const handleBackToSettings = () => setCurrentStep(STEPS.SETTINGS);

  return (
    <div className="fixed inset-0 bg-[#0a0a12] z-50">
      <AnimatePresence mode="wait">
        {currentStep === STEPS.LIBRARY && (
          <StateLibrary
            key="library"
            savedStates={savedStates}
            onCreateNew={() => setCurrentStep(STEPS.ARCHETYPE)}
            onPlayState={handlePlayState}
            onClose={onClose}
          />
        )}

        {currentStep === STEPS.ARCHETYPE && (
          <ArchetypeSelection
            key="archetype"
            onSelect={handleArchetypeSelect}
            onBack={handleBackToLibrary}
          />
        )}

        {currentStep === STEPS.MUSIC && (
          <MusicSelection
            key="music"
            archetype={selectedArchetype}
            onSelect={handleMusicSelect}
            onBack={handleBackToArchetype}
          />
        )}

        {currentStep === STEPS.IMMERSION && (
          <ImmersionExperience
            key="immersion"
            archetypeId={selectedArchetype}
            musicTrack={selectedMusic}
            onComplete={handleImmersionComplete}
          />
        )}

        {currentStep === STEPS.AFFIRMATION && (
          <AffirmationScreen
            key="affirmation"
            archetypeId={selectedArchetype}
            musicTrack={selectedMusic}
            onContinue={handleAffirmationContinue}
          />
        )}

        {currentStep === STEPS.RECORD && (
          <RecordState
            key="record"
            archetypeId={selectedArchetype}
            musicTrack={selectedMusic}
            onComplete={handleRecordComplete}
            onBack={handleBackToMusic}
          />
        )}

        {currentStep === STEPS.SETTINGS && (
          <AudioSettings
            key="settings"
            archetypeId={selectedArchetype}
            musicTrack={selectedMusic}
            onComplete={handleSettingsComplete}
            onBack={handleBackToRecord}
          />
        )}

        {currentStep === STEPS.SAVE && (
          <SaveState
            key="save"
            archetypeId={selectedArchetype}
            onSave={handleSave}
            onBack={handleBackToSettings}
          />
        )}

        {currentStep === STEPS.PLAYER && activePlayState && (
          <RealityPlayer
            key="player"
            state={activePlayState}
            onClose={handleClosePlayer}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TuneInToReality;
